import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

/**
 * Router layout — split into two regions:
 *
 *  1. Daily operation routes live at their natural paths (/manager/inbox,
 *     /manager/contacts, /manager/campaigns…). The main sidebar exposes
 *     only this region — a "calm" surface for operators.
 *
 *  2. Configuration / admin routes live under `/manager/settings/…` and
 *     are mounted as children of SettingsHubView. The hub renders its
 *     own secondary sidebar, so the chrome itself signals "you're in
 *     settings now".
 *
 * Backwards compatibility: all legacy admin paths (/manager/instances,
 * /manager/users, /manager/admin/*) are preserved as `redirect:` entries
 * that bounce to the hub equivalent. Bookmarks keep working; the active
 * highlight consolidates to the hub.
 */

// Settings hub children — declared as a flat array so we can reuse the
// same descriptors for the legacy redirect entries below (single source of
// truth for the slug map).
interface HubChild {
  // slug appended to `/manager/settings/`
  slug: string
  // legacy path that used to host this view (used to emit a redirect entry)
  legacyPath: string
  // unique route name (the LEGACY route owns the name to keep
  // `router.push({ name })` callers — if any are added — working)
  name: string
  // dynamic import of the view component
  loader: () => Promise<unknown>
  // optional permission/restricted meta
  meta?: Record<string, unknown>
}

const hubChildren: HubChild[] = [
  // ── Plataforma WhatsApp ──
  { slug: 'instances', legacyPath: '/manager/instances', name: 'Instances', loader: () => import('../views/InstancesView.vue'), meta: { permission: 'instance:read' } },
  { slug: 'instance-settings', legacyPath: '/manager/instance-settings', name: 'InstanceSettings', loader: () => import('../views/InstanceSettingsView.vue'), meta: { permission: 'instance:manage' } },
  { slug: 'failover', legacyPath: '/manager/failover', name: 'Failover', loader: () => import('../views/FailoverView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:update' } },
  { slug: 'whatsapp-24h', legacyPath: '/manager/whatsapp-24h', name: 'WhatsApp24h', loader: () => import('../views/WhatsApp24hView.vue'), meta: { permission: 'instance:read' } },
  { slug: 'voip', legacyPath: '/manager/voip', name: 'Voip', loader: () => import('../views/VoipView.vue'), meta: { permission: 'instance:read' } },
  { slug: 'webhooks/dead-letters', legacyPath: '/manager/webhooks/dead-letters', name: 'WebhookDeadLetters', loader: () => import('../views/WebhookDeadLettersView.vue'), meta: { permissionsAll: ['instance:read', 'webhook:read'] } },
  { slug: 'health', legacyPath: '/manager/health', name: 'Health', loader: () => import('../views/HealthView.vue'), meta: { permission: 'settings:read' } },
  { slug: 'send', legacyPath: '/manager/send', name: 'SendTest', loader: () => import('../views/SendTestView.vue'), meta: { permission: 'instance:read' } },

  // ── Integrações ──
  { slug: 'chatwoot', legacyPath: '/manager/chatwoot', name: 'Chatwoot', loader: () => import('../views/ChatwootView.vue'), meta: { permission: 'settings:read' } },
  { slug: 'integrations', legacyPath: '/manager/integrations', name: 'Integrations', loader: () => import('../views/IntegrationsView.vue'), meta: { permission: 'settings:read' } },
  { slug: 'mcp-servers', legacyPath: '/manager/mcp-servers', name: 'MCPServers', loader: () => import('../views/MCPServersView.vue'), meta: { permission: 'settings:read' } },
  { slug: 'alerts', legacyPath: '/manager/alerts', name: 'Alerting', loader: () => import('../views/AlertingView.vue'), meta: { permission: 'settings:read' } },

  // ── Acesso ──
  { slug: 'users', legacyPath: '/manager/users', name: 'Users', loader: () => import('../views/UsersView.vue'), meta: { permission: 'user:read' } },
  { slug: 'teams', legacyPath: '/manager/teams', name: 'Teams', loader: () => import('../views/TeamsView.vue'), meta: { permissionsAll: ['team:read', 'user:read'] } },
  { slug: 'roles', legacyPath: '/manager/roles', name: 'Roles', loader: () => import('../views/RolesView.vue'), meta: { permission: 'role:manage' } },
  { slug: 'custom-fields', legacyPath: '/manager/custom-fields', name: 'CustomFields', loader: () => import('../views/CustomFieldsView.vue'), meta: { permission: 'custom_field:read' } },

  // ── Organização ──
  // The old `/manager/settings` page becomes a child slug. We keep the
  // legacy redirect (the `redirect:` for `/manager/settings` is special-
  // cased below since it conflicts with the hub root path).
  { slug: 'organization', legacyPath: '__legacy_settings__', name: 'OrganizationSettings', loader: () => import('../views/SettingsView.vue'), meta: { permission: 'settings:read' } },
  { slug: 'help', legacyPath: '/manager/help', name: 'HelpCenter', loader: () => import('../views/HelpCenterView.vue') },
  { slug: 'onboarding', legacyPath: '/manager/onboarding', name: 'Onboarding', loader: () => import('../views/OnboardingView.vue'), meta: { permission: 'instance:read' } },

  // ── Plataforma (super-admin) ──
  { slug: 'admin/dashboard', legacyPath: '/manager/admin/dashboard', name: 'AdminDashboard', loader: () => import('../views/AdminDashboardView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },
  { slug: 'admin/orgs', legacyPath: '/manager/admin/orgs', name: 'AdminOrgs', loader: () => import('../views/AdminOrgsView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },
  { slug: 'admin/plans', legacyPath: '/manager/admin/plans', name: 'AdminPlans', loader: () => import('../views/AdminPlansView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:plan:read' } },
  { slug: 'admin/platform-users', legacyPath: '/manager/admin/platform-users', name: 'AdminPlatformUsers', loader: () => import('../views/AdminPlatformUsersView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:user:read' } },
  { slug: 'admin/platform-roles', legacyPath: '/manager/admin/platform-roles', name: 'AdminPlatformRoles', loader: () => import('../views/AdminPlatformRolesView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:role:manage' } },
  { slug: 'admin/features', legacyPath: '/manager/admin/features', name: 'AdminFeatures', loader: () => import('../views/AdminFeaturesView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:feature_flag:read' } },
  { slug: 'admin/audit', legacyPath: '/manager/admin/audit', name: 'AdminAudit', loader: () => import('../views/AdminAuditView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:audit:read' } },
  { slug: 'admin/support-sessions', legacyPath: '/manager/admin/support-sessions', name: 'AdminSupportSessions', loader: () => import('../views/AdminSupportSessionsView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:audit:read' } },
  { slug: 'admin/retention', legacyPath: '/manager/admin/retention', name: 'AdminRetention', loader: () => import('../views/AdminRetentionView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },
  { slug: 'admin/releases', legacyPath: '/manager/admin/releases', name: 'AdminReleases', loader: () => import('../views/AdminReleasesView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:update' } },
  { slug: 'admin/metadata', legacyPath: '/manager/admin/metadata', name: 'AdminMetadata', loader: () => import('../views/AdminMetadataView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },
  { slug: 'admin/rate-limits', legacyPath: '/manager/admin/rate-limits', name: 'AdminRateLimits', loader: () => import('../views/AdminRateLimitsView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },
  { slug: 'admin/scheduled', legacyPath: '/manager/admin/scheduled', name: 'AdminScheduled', loader: () => import('../views/ScheduledMessagesView.vue'), meta: { requiresPlatformAdmin: true, platformPermission: 'platform:org:read' } },

  // ── Insights item that legacy sidebar exposed; events monitor lives in
  //    main sidebar too but legacy /manager/events keeps a stable name. ──
  { slug: 'events', legacyPath: '/manager/events', name: 'EventMonitor', loader: () => import('../views/EventMonitorView.vue'), meta: { permission: 'instance:read' } },
  { slug: 'logs', legacyPath: '/manager/logs', name: 'Logs', loader: () => import('../views/LogsView.vue'), meta: { permission: 'instance:read' } },
]

// Build the hub's child route records — each one wraps the view component
// and inherits requiresAuth from the parent. Cast through unknown because
// RouteRecordRaw is a union of mutually exclusive shapes and TS can't
// narrow our object literal automatically.
const hubChildRoutes = hubChildren.map((c) => ({
  path: c.slug,
  // Name lives on the legacy redirect entry, NOT the hub child — Vue Router
  // forbids duplicate names. Add a `Hub` suffix here for internal use.
  name: `${c.name}Hub`,
  component: c.loader,
  meta: { requiresAuth: true, ...(c.meta ?? {}) },
})) as unknown as RouteRecordRaw[]

// Build legacy redirect entries — operators with bookmarks land on the new
// hub path. Names are preserved here so any future `router.push({name})`
// caller continues to work.
const legacyRedirectRoutes: RouteRecordRaw[] = hubChildren
  .filter((c) => c.legacyPath !== '__legacy_settings__')
  .map((c) => ({
    path: c.legacyPath,
    name: c.name,
    redirect: `/manager/settings/${c.slug}`,
    meta: c.meta ?? {},
  }))

const routes: RouteRecordRaw[] = [
  {
    path: '/manager/login',
    name: 'Login',
    component: () => import('../views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/manager/sso-landing',
    name: 'SSOLanding',
    component: () => import('../views/SSOLanding.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/manager/signup',
    name: 'Signup',
    redirect: '/manager/login',
  },
  // v0.50.0 — credential reset UI. Single view handles both phases
  // (email request when no token, password set when token in query).
  {
    path: '/manager/reset',
    name: 'PasswordReset',
    component: () => import('../views/PasswordResetView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/manager',
    name: 'Dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true, permission: 'instance:read' },
  },
  // ── Daily operation routes (main sidebar) ──
  {
    path: '/manager/instance/:id',
    name: 'InstanceDetail',
    component: () => import('../views/InstanceDetailView.vue'),
    meta: { requiresAuth: true, permission: 'instance:read' },
  },
  {
    path: '/manager/chat',
    redirect: '/manager/inbox',
  },
  {
    path: '/manager/inbox',
    name: 'Inbox',
    component: () => import('../views/InboxView.vue'),
    meta: { requiresAuth: true, permission: 'conversation:read' },
  },
  {
    path: '/manager/starred',
    name: 'StarredMessages',
    component: () => import('../views/StarredMessagesView.vue'),
    meta: { requiresAuth: true, permission: 'message:read' },
  },
  {
    path: '/manager/revoked',
    name: 'RevokedMessages',
    component: () => import('../views/RevokedMessagesView.vue'),
    meta: { requiresAuth: true, permission: 'message:delete' },
  },
  {
    path: '/manager/contacts',
    name: 'Contacts',
    component: () => import('../views/ContactsView.vue'),
    meta: { requiresAuth: true, permission: 'contact:read' },
  },
  {
    path: '/manager/companies',
    name: 'Companies',
    component: () => import('../views/CompaniesView.vue'),
    meta: { requiresAuth: true, permission: 'contact:read' },
  },
  {
    path: '/manager/groups',
    name: 'Groups',
    component: () => import('../views/GroupsView.vue'),
    meta: { requiresAuth: true, permissionsAll: ['group:read', 'instance:read'] },
  },
  {
    path: '/manager/communities',
    name: 'Communities',
    component: () => import('../views/CommunitiesView.vue'),
    meta: { requiresAuth: true, permissionsAll: ['group:read', 'instance:read'] },
  },
  {
    path: '/manager/newsletters',
    name: 'Newsletters',
    component: () => import('../views/NewslettersView.vue'),
    meta: { requiresAuth: true, permissionsAll: ['group:read', 'instance:read'] },
  },
  {
    path: '/manager/labels',
    name: 'Labels',
    component: () => import('../views/LabelsView.vue'),
    meta: { requiresAuth: true, permissionsAll: ['label:read', 'instance:read'] },
  },
  {
    path: '/manager/campaigns',
    name: 'Campaigns',
    component: () => import('../views/CampaignsView.vue'),
    meta: { requiresAuth: true, permission: 'campaign:read' },
  },
  {
    path: '/manager/broadcast',
    name: 'Broadcast',
    redirect: '/manager/campaigns',
  },
  {
    path: '/manager/phrase-library',
    name: 'PhraseLibrary',
    component: () => import('../views/PhraseLibraryView.vue'),
    meta: { requiresAuth: true, permission: 'campaign:read' },
  },
  {
    path: '/manager/canned-responses',
    name: 'CannedResponses',
    component: () => import('../views/CannedResponsesView.vue'),
    meta: { requiresAuth: true, permission: 'canned:read' },
  },
  {
    path: '/manager/automations',
    name: 'Automations',
    component: () => import('../views/AutomationsView.vue'),
    meta: { requiresAuth: true, permission: 'automation:read' },
  },
  {
    // v0.75 — visual workflow builder (vue-flow). The legacy form-based
    // editor lives at /manager/automations and remains the entry point;
    // this route is reachable via the "Editar visual" row action.
    path: '/manager/automations/:id/builder',
    name: 'AutomationWorkflowBuilder',
    component: () => import('../views/WorkflowBuilderView.vue'),
    meta: { requiresAuth: true, permission: 'automation:read' },
  },
  {
    path: '/manager/macros',
    name: 'Macros',
    component: () => import('../views/MacrosView.vue'),
    meta: { requiresAuth: true, permission: 'macro:read' },
  },
  {
    path: '/manager/analytics',
    name: 'Analytics',
    component: () => import('../views/AnalyticsView.vue'),
    meta: { requiresAuth: true, permission: 'report:read' },
  },

  // ── Settings hub: parent shell + nested children ──
  {
    path: '/manager/settings',
    name: 'SettingsHub',
    component: () => import('../views/settings/SettingsHubView.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['instance:read', 'user:read', 'role:manage', 'custom_field:read', 'settings:read', 'webhook:read'],
    },
    children: [
      {
        path: '',
        name: 'SettingsHubLanding',
        component: () => import('../views/settings/SettingsHubLandingView.vue'),
        meta: { requiresAuth: true },
      },
      ...hubChildRoutes,
    ],
  },

  // ── Legacy redirect entries — preserve bookmarks ──
  ...legacyRedirectRoutes,

  // The OLD `/manager/settings` page (the Organization settings view) now
  // lives under `/manager/settings/organization`. We can't add a redirect
  // here without colliding with the hub parent, so `/manager/profile` —
  // previously pointing at the legacy settings page — now jumps directly
  // to the hub child to preserve intent.
  {
    path: '/manager/profile',
    name: 'Profile',
    redirect: '/manager/settings/organization',
  },

  {
    path: '/manager/:pathMatch(.*)*',
    redirect: '/manager',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  strict: false,
})

// Slugs whose pages don't make sense for restricted users (org-wide ops
// and admin chrome). The denylist matches both legacy paths AND the new
// hub paths in a single source of truth.
const restrictedDenySlugs = [
  'instances',
  'instance-settings',
  'failover',
  'webhooks/dead-letters',
  'events',
  'health',
  'logs',
  'whatsapp-24h',
  'integrations',
  'mcp-servers',
  'alerts',
  'voip',
  'custom-fields',
  'organization', // legacy /manager/settings → org config
  // Hub root itself is reachable (Chatwoot + Help live there).
]

function isRestrictedPath(path: string): boolean {
  for (const slug of restrictedDenySlugs) {
    const legacy = `/manager/${slug}`
    const hub = `/manager/settings/${slug}`
    if (path === legacy || path.startsWith(legacy + '/')) return true
    if (path === hub || path.startsWith(hub + '/')) return true
  }
  return false
}

function stringArrayFromMeta(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0)
  if (typeof value === 'string' && value.length > 0) return [value]
  return []
}

async function forbiddenRedirectPath(): Promise<string> {
  const { usePermissionStore } = await import('../stores/permissions')
  const permissionStore = usePermissionStore()
  if (permissionStore.has('conversation:read')) return '/manager/inbox'
  return '/manager/inbox'
}

// Navigation guard: manager auth is JWT-based.
// hydrateSessionIfNeeded ensures the user + permission stores are populated before guards that
// depend on user claims (isPlatformAdmin, allowedInstanceIds) run. Without
// this, a hard-navigation to /manager/admin/orgs with a valid token in
// localStorage would see user.value === null, isPlatformAdmin === false, and
// redirect to /manager.
async function hydrateSessionIfNeeded(): Promise<void> {
  const { useUserStore } = await import('../stores/user')
  const { usePermissionStore } = await import('../stores/permissions')
  const store = useUserStore()
  const permissionStore = usePermissionStore()
  if (!store.token) return
  if (!store.user || !store.user.role?.permissions?.length) {
    await store.fetchMe()
  }
  permissionStore.setPermissions(store.user?.role?.permissions ?? [])
}

// Decode a JWT's `exp` claim and return true if it's already past.
// Used to short-circuit auth guards when localStorage holds a dead token —
// without this, the guard would let the user through, fetchMe would 401, and
// the axios interceptor would produce noisy refresh-then-redirect cascades.
function isJwtExpired(token: string): boolean {
  const parts = token.split('.')
  if (parts.length !== 3) return true
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4
    if (pad) b64 += '='.repeat(4 - pad)
    const payload = JSON.parse(atob(b64)) as { exp?: number }
    if (typeof payload.exp !== 'number') return false
    return payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

router.beforeEach(async (to) => {
  let jwtToken = localStorage.getItem('neogo_token')
  // Treat expired tokens as no token at all. Clears the trio so the axios
  // interceptor doesn't later try to refresh with a stale refresh token.
  if (jwtToken && isJwtExpired(jwtToken)) {
    localStorage.removeItem('neogo_token')
    localStorage.removeItem('neogo_refresh_token')
    localStorage.removeItem('neogo_apikey')
    jwtToken = null
  }
  const isAuthenticated = !!jwtToken

  if (to.meta.requiresAuth !== false && !isAuthenticated) {
    return '/manager/login'
  }

  if (to.path === '/manager/login' && isAuthenticated) {
    return '/manager'
  }

  // Hydrate user claims for any authenticated route. Cheap when the store is
  // already populated (single ref read). We can't gate this on
  // requiresPlatformAdmin alone — components like AppLayout's impersonation
  // banner read user.organizationId on every page, and after a hard reload
  // (e.g. post-impersonation `window.location.assign('/manager/')`) the store
  // starts empty. Without this, the banner would never render on the
  // dashboard and the operator gets no signal they're impersonating.
  if (isAuthenticated) {
    try {
      await hydrateSessionIfNeeded()
    } catch {
      // fetchMe failure is non-fatal for the guard — fall through and let
      // the backend reject the request if the token is truly invalid.
    }
  }

  // Walk matched routes so platform-admin meta on a hub *child* is honored
  // even when the parent route doesn't carry it.
  const requiresPlatformAdmin = to.matched.some((r) => r.meta.requiresPlatformAdmin)
  if (requiresPlatformAdmin) {
    const { useUserStore } = await import('../stores/user')
    const store = useUserStore()
    const platformPermissionsAll = to.matched.flatMap((r) => [
      ...stringArrayFromMeta(r.meta.platformPermission),
      ...stringArrayFromMeta(r.meta.platformPermissionsAll),
    ])
    const platformPermissionsAny = to.matched.flatMap((r) => stringArrayFromMeta(r.meta.platformPermissionsAny))
    const hasAll = platformPermissionsAll.every((perm) => store.hasPlatformPermission(perm))
    const hasAny = platformPermissionsAny.length === 0 || platformPermissionsAny.some((perm) => store.hasPlatformPermission(perm))
    if (store.isPlatformAdmin && hasAll && hasAny) {
      return true
    }
    return '/manager'
  }

  const routePermissionsAll = to.matched.flatMap((r) => [
    ...stringArrayFromMeta(r.meta.permission),
    ...stringArrayFromMeta(r.meta.permissionsAll),
  ])
  const routePermissionsAny = to.matched.flatMap((r) => stringArrayFromMeta(r.meta.permissionsAny))
  if (isAuthenticated && (routePermissionsAll.length > 0 || routePermissionsAny.length > 0)) {
    const { useUserStore } = await import('../stores/user')
    const { usePermissionStore } = await import('../stores/permissions')
    const store = useUserStore()
    const permissionStore = usePermissionStore()
    if (!store.isPlatformAdmin) {
      const hasAll = routePermissionsAll.every((perm) => permissionStore.has(perm))
      const hasAny = routePermissionsAny.length === 0 || routePermissionsAny.some((perm) => permissionStore.has(perm))
      if (!hasAll || !hasAny) {
        return await forbiddenRedirectPath()
      }
    }
  }

  if (isAuthenticated && (to.path === '/manager' || to.path === '/manager/')) {
    const { useUserStore } = await import('../stores/user')
    const { usePermissionStore } = await import('../stores/permissions')
    const store = useUserStore()
    const permissionStore = usePermissionStore()
    const canOpenDashboard = store.isPlatformAdmin || permissionStore.has('instance:read')
    if (!canOpenDashboard) {
      return '/manager/inbox'
    }
  }

  // Restricted-user denylist: agents pinned to specific instances shouldn't
  // land on org-wide ops screens. Redirect to dashboard with a console hint
  // (toast would require a store coupling — sidebar hide already explains).
  if (isAuthenticated && isRestrictedPath(to.path)) {
    const { useUserStore } = await import('../stores/user')
    const store = useUserStore()
    const ids = store.allowedInstanceIds
    const restricted = !store.isPlatformAdmin && Array.isArray(ids) && ids.length > 0
    if (restricted) {
      console.warn('[navguard] route blocked for restricted user:', to.path)
      return '/manager'
    }
  }

  // Allow navigation (implicit return undefined = proceed)
})

export default router
